export const HELP_CONTENT = {
  'general-introduction': {
    title: 'Welcome to CommunityCare',
    content: `
# Welcome to CommunityCare

CommunityCare is a comprehensive Hospital Management System designed to streamline operations and improve patient care.

## Getting Started
- **Navigation**: Use the sidebar to access different modules.
- **Profile**: Click your avatar in the top right to view your profile or log out.
- **Help**: Click this help button anytime for context-sensitive assistance.
- **Notifications**: The bell icon shows recent alerts and updates.

## Common Terms
- **MRN**: Medical Record Number, a unique identifier for patients.
- **Triage**: The process of assessing patient urgency.
- **Ward**: A section of the hospital where patients are housed.
- **Dispense**: The act of providing medication to a patient.

## Role-Based Access
Each user role has access to specific modules:
- **Admin**: Full system access
- **Doctor**: Clinical operations, prescriptions, consultations
- **Nurse/Matron**: Vitals, medication administration, ward management
- **Pharmacist**: Pharmacy operations, dispensing, inventory
- **Lab Technician**: Laboratory tests and results
- **Billing Clerk/Accountant**: Financial operations  
    `,
    keywords: ['welcome', 'start', 'intro', 'help', 'overview', 'getting started']
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // DOCTOR MODULE
  // ═══════════════════════════════════════════════════════════════════════════
  'dashboard-doctor': {
    title: 'Doctor Dashboard',
    content: `
# Doctor Dashboard

The Doctor Dashboard is your central hub for managing patient consultations and appointments.

## Key Features
- **Consultation Queue**: View patients waiting for consultation with priority indicators.
- **My Appointments**: See your scheduled appointments for the day.
- **Quick Actions**: Access common tasks like new prescription, lab request, and imaging.

## Patient Queue Management
1. Click on a patient card to view their details.
2. Use "Start Consultation" to begin the encounter.
3. Status updates automatically as you progress.

## Quick Actions
- **New Prescription**: Create a prescription directly from the dashboard.
- **Lab Request**: Order laboratory tests with a few clicks.
- **Imaging Request**: Request X-rays, ultrasounds, or CT scans.

## Tips
- Click on a patient's name to view their full medical history.
- Use the status tabs to filter between 'Waiting', 'In Progress', and 'Completed'.
- Emergency patients are highlighted in red and should be prioritized.
    `,
    keywords: ['doctor', 'consultation', 'appointments', 'queue', 'patients']
  },

  'consultation-workspace': {
    title: 'Consultation Workspace',
    content: `
# Consultation Workspace

The Consultation Workspace is an integrated environment for documenting patient encounters and ordering prescriptions/investigations.

## Getting Started
1. Select a patient from the dropdown or arrive from the **Consultation Queue**.
2. If from queue, patient details and chief complaint are pre-loaded.
3. Begin documenting in the note editor.

## Layout
- **Patient Header**: Shows patient name, MRN, age, gender, and any Family Card badge.
- **Tabs**:
  - **Editor**: Write clinical notes (SOAP format, templates available).
  - **Lab Results**: View completed lab results in real-time.
  - **Prescriptions**: Add medications to send to Pharmacy.
  - **Lab Orders**: Request laboratory tests.
  - **History**: View patient's previous consultation notes.

## Creating Clinical Notes
1. Select a template (Admission Note, Progress Note, Discharge Summary) or start blank.
2. Document history, examination findings, assessment, and plan.
3. Use the **Save** button to save a draft.
4. Click **Finalize** when complete - finalized notes cannot be edited.

## Prescriptions
1. Click the **Prescriptions** tab.
2. Click **"Add Prescription"**.
3. Search medication by name - shows stock availability and price.
4. Enter dosage, frequency, route, duration, and quantity.
5. Click **"Add to Order"** - prescription is sent to Pharmacy **immediately**.
6. A billing charge is automatically created for Accounts.
7. Pharmacy receives a notification and can start preparation.

## Lab Orders
1. Click the **Lab Orders** tab.
2. Click **"Add Lab Test"**.
3. Search for test by name or code.
4. Set priority: Routine, Urgent, or STAT.
5. Tests are automatically grouped by **Sample Type** (Blood, Urine, Stool, etc.).
6. Click **"Submit Order"** - order is sent to Lab **immediately**.
7. Separate orders are created for each sample type.
8. Billing charges are created per test for Accounts.
9. Lab receives notifications (urgent orders flagged prominently).

## Viewing Lab Results
- The **Lab Results** tab shows completed results in real-time.
- Results are available as soon as Lab marks them complete.
- View individual test parameters and values.

## Finalizing a Consultation
1. Ensure all notes are complete.
2. Click **"Finalize Note"**.
3. Confirm in the modal.
4. Note is locked from editing.
5. If from queue, patient is marked as consultation completed.

## Tips
- **Auto-save**: Notes are auto-saved every 30 seconds.
- **Draft vs Finalized**: Draft notes can be edited; finalized cannot.
- **Real-time sync**: New lab results appear automatically without refreshing.
- **Clinical Decision Support**: Drug interactions and allergy alerts are shown.
    `,
    keywords: ['consultation', 'notes', 'prescription', 'clinical', 'encounter', 'SOAP', 'lab', 'orders']
  },

  'prescription-management': {
    title: 'Prescription Management',
    content: `
# Prescription Management

Create, manage, and track patient prescriptions.

## Creating a Prescription
1. Search for the patient by MRN or name.
2. Click "New Prescription".
3. Add medications with dosage, frequency, duration.
4. Review and sign the prescription.

## Prescription Status
- **Pending**: Awaiting pharmacy processing.
- **Dispensed**: Medication has been given to the patient.
- **Partially Dispensed**: Some items are pending.

## Drug Interactions
The system automatically checks for known drug interactions and alerts you.

## Refills
To refill a previous prescription, open the patient's history and click "Refill" on the desired prescription.
    `,
    keywords: ['prescription', 'medication', 'drug', 'refill', 'dosage']
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // NURSING MODULE
  // ═══════════════════════════════════════════════════════════════════════════
  'dashboard-nurse': {
    title: 'Nursing Dashboard',
    content: `
# Nursing Dashboard

Central hub for nursing activities, vitals management, and patient care.

## Key Features
- **Vitals Recording**: Rapidly enter vitals for admitted patients.
- **Medication Schedule**: View and sign off on due medications.
- **Ward Overview**: See bed occupancy and patient status.
- **Handover Notes**: Document shift handover information.

## Recording Vitals
1. Select a patient from the ward view.
2. Click "Record Vitals".
3. Enter Temperature, BP, Pulse, RR, SpO2.
4. Save the reading.

## Medication Administration
1. View the "Due Medications" section.
2. Verify medication, dose, and patient identity.
3. Click "Administer" and confirm.
4. Record any notes if needed.

## Triage
- Click "Triage" to assess an incoming patient.
- Assign urgency level: Emergency, Urgent, Non-Urgent.
- Add presenting complaints and initial observations.

## Handover
- Click "Handover" at shift end.
- Summarize patient statuses and pending actions.
- Next shift can view these notes.
    `,
    keywords: ['nurse', 'nursing', 'vitals', 'medication', 'ward', 'triage', 'handover']
  },

  'bed-management': {
    title: 'Admissions Management',
    content: `
# Admissions Management

Manage patient admissions, bed allocation, and ward capacity.

## Ward Overview
- See all wards and their current occupancy.
- Beds are color-coded: Available (green), Occupied (blue), Reserved (yellow).

## How Admissions Work

### Step 1: Doctor Orders Admission
1. During a **consultation**, the doctor clicks "Order Admission".
2. Doctor enters:
   - **Diagnosis**: Primary diagnosis for admission.
   - **Reason for Admission**: Clinical justification.
   - **Urgency**: Routine, Urgent, or Emergency.
   - **Preferred Bed Type**: Standard Ward, Semi-Private, Private, ICU, Emergency, or SCBU.
   - **Special Instructions**: Isolation needs, mobility issues, etc.
3. Click "Create Admission Order" to submit.

### Step 2: Nursing Assigns Bed
1. Nursing staff receive a **notification** for the new admission order.
2. Open the **Nursing Dashboard** or **Admissions Management**.
3. View **Pending Admissions** panel showing all waiting orders.
4. Click **"Assign Bed"** on the patient's order.
5. Select an available bed in the appropriate ward.
6. Confirm to complete the admission.

## Transferring a Patient
1. Find the patient's current bed.
2. Click "Transfer".
3. Select the new ward and bed.
4. Confirm the transfer.

## Discharging a Patient
1. Locate the patient.
2. Click "Discharge".
3. Complete discharge summary.
4. Confirm - the bed becomes available.

## Tips
- Emergency admissions are highlighted in red and should be prioritized.
- Check special instructions for isolation or equipment requirements.
- Hovering over a bed shows patient details.
    `,
    keywords: ['bed', 'ward', 'admission', 'discharge', 'transfer', 'occupancy', 'order', 'nursing']
  },

  'medication-schedule': {
    title: 'Medication Schedule',
    content: `
# Medication Schedule

View and manage scheduled medication administrations for admitted patients.

## Understanding the Schedule
- The schedule shows all due medications by time slot.
- Overdue items are highlighted in red.
- Completed items are grayed out.

## Administering Medication
1. Click on a medication item.
2. Review patient details and medication.
3. Confirm the patient's identity.
4. Click "Administer" to record.
5. Add notes if there are any observations.

## Missed Doses
If a dose is missed, document the reason (e.g., patient refused, patient not available).

## PRN Medications
As-needed (PRN) medications appear separately and can be given at nurse's discretion.
    `,
    keywords: ['medication', 'schedule', 'administer', 'dose', 'PRN']
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // PATIENT RECORDS
  // ═══════════════════════════════════════════════════════════════════════════
  'patient-registration': {
    title: 'Patient Registration',
    content: `
# Patient Registration

Register new patients or update existing patient demographics.

## Registering a New Patient
1. **Search First**: Always search by name, phone, or MRN to check for existing records.
2. If no record found, click "Register New Patient".
3. Fill in required fields: Full Name, Date of Birth, Gender, Phone.
4. Add optional: Address, Next of Kin, Insurance details.
5. Submit to generate an MRN.

## Updating Patient Details
1. Search for the patient.
2. Click "Edit" on their record.
3. Update the necessary fields.
4. Save changes.

## Emergency Registration
For critical cases where full details are unavailable:
1. Click "Quick Register" or "Emergency Registration".
2. Enter minimal details (name or identifier).
3. A temporary MRN is generated.
4. Complete details can be added later.

## Family Card
Patients can be linked via Family Card for group payments and shared billing.
    `,
    keywords: ['registration', 'new patient', 'demographics', 'reception', 'MRN']
  },

  'patient-records': {
    title: 'Patient Records',
    content: `
# Patient Records

Access and manage comprehensive patient medical records.

## Searching for a Patient
Use the search bar to find patients by:
- MRN (Medical Record Number)
- Full Name
- Phone Number

## Patient Profile Tabs
- **Summary**: Overview of demographics and recent activity.
- **Encounters**: History of all clinical visits.
- **Lab Results**: All laboratory test results.
- **Imaging**: X-rays, ultrasounds, CT scans, and reports.
- **Prescriptions**: Medication history.
- **Billing**: Financial ledger and invoices.
- **Documents**: Uploaded files, consent forms, referrals.

## Viewing Lab Results
1. Navigate to the "Lab Results" tab.
2. Results are listed by date.
3. Click on a result to see detailed values.
4. Abnormal values are flagged.

## Viewing Imaging
1. Navigate to the "Imaging" tab.
2. See a list of imaging studies.
3. Click to view images and radiologist reports.

## Tips
- Use the timeline view for a chronological overview.
- Filter by date range or category.
    `,
    keywords: ['patient', 'records', 'history', 'EMR', 'chart', 'profile']
  },

  'patient-full-view': {
    title: 'Patient Full View',
    content: `
# Patient Full View

A comprehensive, single-page view of a patient's entire medical record.

## Layout
- **Header**: Patient photo, name, MRN, key demographics.
- **Vitals Graph**: Visual trend of recent vitals.
- **Sections**: Collapsible sections for each data category.

## Quick Actions
From this view you can:
- Start a new consultation
- Order lab tests
- Order imaging
- Create a prescription
- Schedule a follow-up

## Printing
Click "Print Summary" to generate a printable patient summary.
    `,
    keywords: ['patient', 'full view', 'comprehensive', '360']
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // PHARMACY MODULE
  // ═══════════════════════════════════════════════════════════════════════════
  'pharmacy-dashboard': {
    title: 'Pharmacy Dashboard',
    content: `
# Pharmacy Dashboard

Manage prescriptions, inventory, and medication dispensing.

## Pending Prescriptions
- View all prescriptions awaiting dispensing.
- Prescriptions are sorted by priority and time.
- Click on a prescription to view details.

## Dispensing Workflow
1. Select a prescription from the queue.
2. Verify patient identity.
3. Pick the correct medications from inventory.
4. Scan barcodes (if using scanner).
5. Confirm dispense - the prescription is marked complete.

## Inventory Management
- **Low Stock Alerts**: Items below reorder point are flagged.
- **Stock Count**: Update inventory counts regularly.
- **Add Stock**: Record incoming stock deliveries.

## Scanning
Use barcode scanning for:
- Fast medication lookup
- Dispense verification
- Inventory updates

## Tips
- Check for drug interactions before dispensing.
- Verify patient allergies in the patient record.
    `,
    keywords: ['pharmacy', 'medicine', 'dispense', 'stock', 'prescription', 'inventory']
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // LABORATORY MODULE
  // ═══════════════════════════════════════════════════════════════════════════
  'laboratory-dashboard': {
    title: 'Laboratory Dashboard',
    content: `
# Laboratory Dashboard

Process lab requests, track samples, enter results, and monitor laboratory operations.

## Dashboard Tabs

### Overview
- **Quick Stats**: Pending orders, processing, completed today, critical results.
- **Recent Test Orders**: Quick view of latest orders.
- **Sample Tracking**: Current sample progress.

### Test Orders
- View all pending lab orders from doctors.
- Each order card shows: patient name, MRN, tests, priority, sample type.
- **STAT/Urgent** orders are highlighted.
- Click on an order to open **Order Details Modal**.

### Sample Tracking
- Track samples through **3 stages**:
  1. **Collection**: Sample has been collected.
  2. **Processing**: Sample is being analyzed.
  3. **Reporting**: Results are ready to enter/verify.
- Click **"Enter Results"** when a sample reaches the Reporting stage.
- Completed samples show **"View Results"** button.

### Results Entry
- Select a test from the list of processing/collected orders.
- Enter values for each test parameter.
- Flag abnormal values.
- Save to complete the order - results become visible to the ordering doctor.

### Test Archive
- View completed and historical lab tests.
- Search by patient, date, or test type.
- View results and download reports.

### Quality Metrics
- Monitor turnaround time (TAT).
- View accuracy rates and repeat test rates.
- Track critical result alerts.

## Processing a Lab Order

### Step 1: Receive Order
1. Lab order appears in **Test Orders** tab (from doctor's order).
2. Click to view order details.

### Step 2: Collect Sample
1. Click **"Collect Sample"** on the order.
2. Sample ID is assigned.
3. Order status changes to **Collected**.

### Step 3: Process Sample
1. Move sample through stages in **Sample Tracking**.
2. Click stage buttons to advance the sample.

### Step 4: Enter Results
1. Click **"Enter Results"** when at Reporting stage.
2. Enter values for each test parameter.
3. Click **"Save Results"** to complete.
4. Results are immediately visible to the ordering doctor.

## Doctor View
- Doctors can access Lab Dashboard but see only **their own orders**.
- View is read-only (cannot update status or enter results).
- Tabs limited to: Test Orders, Sample Tracking, Archive.

## Equipment & Inventory
- Track analyzer status and utilization.
- Monitor reagent levels and expiry dates.
- Schedule maintenance and calibration.

## Tips
- Auto-refresh occurs every 60 seconds.
- Critical results require immediate attention.
- Notification context: clicking lab notifications opens the specific order.
    `,
    keywords: ['lab', 'laboratory', 'tests', 'results', 'specimen', 'sample', 'tracking', 'processing']
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // ACCOUNTS & BILLING MODULE
  // ═══════════════════════════════════════════════════════════════════════════
  'accounts-dashboard': {
    title: 'Accounts & Billing Dashboard',
    content: `
# Accounts & Billing Dashboard

Manage patient billing, payments, invoices, and financial workflows.

## Dashboard Layout

### Billing Metrics
Top cards show:
- **Today's Collections**: Total payments received.
- **Outstanding Balance**: Total unpaid amounts.
- **Pending Invoices**: Number of unpaid invoices.
- **Weekly Revenue**: Trend indicator.

### Quick Actions
- **New Invoice**: Create a new invoice for a patient.
- **Record Payment**: Process a payment for an existing invoice.

### Outstanding Payments Panel
- Shows all patients with pending charges grouped by patient.
- Each patient card shows:
  - Patient name and MRN
  - Total outstanding amount
  - Number of pending items
  - Individual charges (expand to view)
- **Pay Now**: Creates an invoice and opens payment modal.

### Pending Discharge Clearance
- Shows patients awaiting financial clearance for discharge.
- Must settle outstanding balance before discharge can proceed.

### Recent Invoices
- List of recent invoices with status filter (All, Unpaid, Paid, Draft).
- Click to view invoice details.

### Family Cards Panel
- Manage Family Cards (₦25,000 registration fee).
- View member balances and aggregate outstanding amounts.
- Process Family Card payments.

## How Charges Are Created

Charges are automatically created when doctors order services:

### From Consultation
1. **Prescriptions**: When a doctor adds a prescription, a pharmacy billing charge is created.
2. **Lab Orders**: When a doctor orders tests, lab billing charges are created (per test).

### From Patient Registration
- **Registration Fee**: ₦5,000 charge created on new patient registration.
- **Family Card Fee**: ₦25,000 charge for new Family Cards.

### From Admissions
- Bed charges, medication schedules, procedures during stay.

## Processing Payments

### Quick Pay (From Outstanding Payments)
1. Locate patient in **Outstanding Payments** panel.
2. Click **"Pay Now"** - creates invoice and opens payment modal.
3. Select payment method (Cash, Card, Bank Transfer, POS, Insurance).
4. Enter amount received.
5. Submit - receipt is generated.

### From Invoice
1. Search for invoice in **Invoice Search Bar**.
2. Click invoice to view details.
3. Click **"Record Payment"**.
4. Complete payment form.
5. Submit and issue receipt.

## Invoice Management

### Creating an Invoice
1. Click **"New Invoice"** in Quick Actions.
2. Search and select patient.
3. Add line items (services, tests, medications).
4. Apply discounts if authorized.
5. Save as draft or finalize.

### Invoice Statuses
- **Draft**: Not yet finalized.
- **Pending**: Awaiting payment.
- **Partially Paid**: Some payment received.
- **Paid**: Fully settled.
- **Voided**: Cancelled.

## Notification Context
- Clicking billing notifications navigates to this dashboard.
- Patient with pending charge is highlighted.
- Quick actions are pre-populated.

## Tips
- Group charges by visit using the queue/visit ID.
- Family Card payments unlock card for member additions.
- Check pending discharges regularly to avoid blocking nursing workflow.
    `,
    keywords: ['accounts', 'billing', 'payment', 'invoice', 'finance', 'charges', 'outstanding', 'receipt']
  },

  'service-management': {
    title: 'Service Management',
    content: `
# Service Management

Configure and manage billable services offered by the hospital.

## Adding a New Service
1. Click "Add Service".
2. Enter service name and code.
3. Set the price.
4. Assign to a department/category.
5. Save.

## Editing Services
1. Find the service in the list.
2. Click "Edit".
3. Update name, price, or category.
4. Save changes.

## Service Categories
Organize services into categories like:
- Consultation
- Laboratory
- Imaging
- Procedures
- Pharmacy

## Price Lists
You can create multiple price lists for different patient types (e.g., Cash, Insurance A, Insurance B).
    `,
    keywords: ['service', 'price', 'billing', 'tariff', 'fee']
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // ADMIN MODULE
  // ═══════════════════════════════════════════════════════════════════════════
  'admin-dashboard': {
    title: 'Admin Dashboard',
    content: `
# Admin Dashboard

System administration and oversight.

## Key Metrics
- Total patients registered (today/all-time)
- Active staff count
- Today's appointments and consultations
- Revenue summary

## Quick Links
- User Management
- Facility Configuration
- System Settings
- Audit Logs

## System Health
Monitor system performance and uptime from this dashboard.
    `,
    keywords: ['admin', 'administration', 'system', 'overview']
  },

  'user-administration': {
    title: 'User Administration',
    content: `
# User Administration

Manage system users, roles, and permissions.

## Adding a New User
1. Click "Add User".
2. Enter email, name, and phone.
3. Select a role (e.g., Doctor, Nurse, Pharmacist).
4. Send invitation email.
5. User completes registration via the link.

## Roles
- **Admin**: Full system access.
- **Doctor**: Clinical modules.
- **Nurse/Matron**: Nursing modules.
- **Pharmacist**: Pharmacy modules.
- **Lab Technician**: Laboratory modules.
- **Billing Clerk**: Accounts modules.
- **Receptionist**: Registration modules.

## Editing a User
1. Find the user in the list.
2. Click "Edit".
3. Update role, status, or details.
4. Save.

## Deactivating a User
Set user status to "Inactive" to revoke access without deleting the account.
    `,
    keywords: ['user', 'role', 'permission', 'staff', 'access']
  },

  'facility-management': {
    title: 'Facility Management',
    content: `
# Facility Management

Configure wards, departments, and physical infrastructure.

## Managing Wards
1. Click "Add Ward".
2. Enter name, capacity, and type (e.g., Male, Female, ICU).
3. Add beds to the ward.

## Managing Departments
Add or edit departments to organize staff and services.

## Locations
Configure clinic rooms, theaters, and other locations for appointment scheduling.
    `,
    keywords: ['facility', 'ward', 'department', 'infrastructure', 'bed']
  },

  'hospital-configuration': {
    title: 'Hospital Configuration',
    content: `
# Hospital Configuration

System-wide settings for the hospital.

## General Settings
- Hospital name and logo
- Contact information
- Address

## Operational Settings
- Working hours
- Appointment slot duration
- Default currency
- Tax settings

## Integrations
Configure external integrations:
- Email/SMS providers
- Insurance portals
- Laboratory information systems
    `,
    keywords: ['configuration', 'settings', 'hospital', 'setup']
  },

  'pulse-dashboard': {
    title: 'Pulse Dashboard (Analytics)',
    content: `
# Pulse Dashboard

Real-time analytics and insights for hospital operations.

## Key Metrics
- Patient flow (arrivals, discharges)
- Consultation wait times
- Bed occupancy rates
- Revenue trends

## Charts
Interactive charts for:
- Daily/weekly/monthly trends
- Department comparisons
- Peak hours analysis

## Filters
Filter data by:
- Date range
- Department
- User/Doctor
    `,
    keywords: ['analytics', 'pulse', 'metrics', 'dashboard', 'insights', 'reports']
  },

  'duty-roster': {
    title: 'Duty Roster',
    content: `
# Duty Roster

Manage staff schedules and shifts.

## Viewing the Roster
- See staff assignments by day, week, or month.
- Filter by department or role.

## Creating a Schedule
1. Select the date range.
2. Assign staff to shifts (Morning, Afternoon, Night).
3. Save the roster.

## Swapping Shifts
Staff can request shift swaps, which require approval.

## Leave Management
View staff on leave and plan coverage accordingly.
    `,
    keywords: ['roster', 'duty', 'schedule', 'shift', 'staff']
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // LOGIN & PROFILE
  // ═══════════════════════════════════════════════════════════════════════════
  'login': {
    title: 'Login',
    content: `
# Logging In

## Steps
1. Enter your registered email address.
2. Enter your password.
3. Click "Login".

## Forgot Password
Click "Forgot Password" and enter your email. A reset link will be sent.

## First-Time Users
If you received an invitation, click the link in the email to set up your account.

## Troubleshooting
- Ensure Caps Lock is off.
- Check that you are using the correct email.
- Contact your administrator if locked out.
    `,
    keywords: ['login', 'password', 'access', 'sign in']
  },

  'user-profile': {
    title: 'User Profile',
    content: `
# User Profile

Manage your personal account settings.

## Updating Your Profile
1. Click your avatar in the top-right corner.
2. Select "Profile".
3. Update your name, photo, or contact details.
4. Save changes.

## Changing Password
1. Go to Profile > Security.
2. Enter current password.
3. Enter new password and confirm.
4. Save.

## Notification Preferences
Configure which notifications you receive (email, in-app, SMS).
    `,
    keywords: ['profile', 'account', 'settings', 'password']
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // AUTHENTICATION & ONBOARDING
  // ═══════════════════════════════════════════════════════════════════════════
  'accept-invite': {
    title: 'Accepting an Invitation',
    content: `
# Accepting an Invitation

Complete your account setup after receiving an invitation email.

## Steps
1. Click the link in your invitation email.
2. Verify your email address is correct.
3. Create a secure password (minimum 8 characters, including numbers and symbols).
4. Enter your personal details (name, phone number).
5. Click "Complete Registration".

## Troubleshooting
- **Link expired?** Contact your administrator to resend the invitation.
- **Already have an account?** Use the "Login" option instead.
- **Wrong email?** Request a new invitation to the correct address.

## After Registration
Once registered, you'll be redirected to your role-appropriate dashboard.
    `,
    keywords: ['invite', 'invitation', 'register', 'signup', 'onboarding']
  },

  'authentication-portal': {
    title: 'Authentication Portal',
    content: `
# Authentication Portal

Secure access to the hospital management system.

## Login Methods
- **Email & Password**: Standard login with your registered credentials.
- **Single Sign-On (SSO)**: If configured by your organization.

## Two-Factor Authentication (2FA)
If 2FA is enabled:
1. Enter your email and password.
2. Check your phone or authenticator app for a verification code.
3. Enter the code to complete login.

## Session Security
- Sessions expire after inactivity for security.
- Logging out properly ensures your session is terminated.
- Avoid using "Remember Me" on shared computers.
    `,
    keywords: ['auth', 'authentication', 'security', '2FA', 'SSO']
  },

  'patient-self-register': {
    title: 'Patient Self-Registration',
    content: `
# Patient Self-Registration

Patients can register themselves using the self-service portal.

## How to Register
1. Scan the QR code at the reception or access the registration link.
2. Fill in your personal details:
   - Full name
   - Date of birth
   - Gender
   - Phone number
   - Address (optional)
3. Provide emergency contact information.
4. Accept the terms and conditions.
5. Submit the registration.

## After Registration
- You will receive a unique Medical Record Number (MRN).
- Present this MRN at the reception for faster check-in.
- You can use this MRN for all future visits.

## Privacy Notice
Your data is protected according to healthcare privacy regulations.
    `,
    keywords: ['self-register', 'patient portal', 'self-service', 'walk-in']
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // PHARMACY - EXPANDED
  // ═══════════════════════════════════════════════════════════════════════════
  'pharmacy-inventory': {
    title: 'Pharmacy Inventory Management',
    content: `
# Pharmacy Inventory Management

Manage medication stock, expiry tracking, and reordering.

## Stock Overview
- View all medications with current quantities.
- Color indicators: Green (adequate), Yellow (low), Red (critical).
- Expiry dates are flagged when approaching.

## Adding Stock
1. Click "Add Stock" or "Receive Delivery".
2. Scan barcodes or search for medications.
3. Enter quantity and batch number.
4. Set expiry date.
5. Confirm receipt.

## Stock Adjustments
For corrections, wastage, or returns:
1. Select the medication.
2. Click "Adjust Stock".
3. Enter the adjustment (+ or -).
4. Provide a reason.

## Reorder Management
- Set reorder points for each medication.
- View "Reorder Suggestions" for items below threshold.
- Generate purchase orders directly from the system.

## Expiry Management
- View "Expiring Soon" report for items within 90 days of expiry.
- Mark items as "Removed from Stock" when expired.
    `,
    keywords: ['inventory', 'stock', 'expiry', 'reorder', 'supply', 'medication']
  },

  'pharmacy-scanning': {
    title: 'Pharmacy Barcode & QR Scanning',
    content: `
# Barcode & QR Scanning

Use scanning for fast and accurate pharmacy operations.

## Supported Scanners
- **Desk Scanners**: USB or Bluetooth barcode scanners.
- **Mobile Camera**: Use your phone or tablet camera for scanning.

## Scanning Operations

### When Receiving Stock
1. Click "Scan to Receive".
2. Scan the medication barcode.
3. System auto-fills product details.
4. Confirm or adjust quantity.

### When Dispensing
1. Open the prescription.
2. Scan each medication before dispensing.
3. System verifies correct item and dosage.
4. Mismatches trigger an alert.

### Inventory Count
1. Open "Stock Count" mode.
2. Scan items one by one.
3. System updates count automatically.

## Troubleshooting
- **Barcode not recognized?** Enter the product code manually.
- **Scanner not working?** Check USB connection or Bluetooth pairing.
- **Camera blurry?** Clean the lens and ensure good lighting.
    `,
    keywords: ['scan', 'barcode', 'QR', 'scanner', 'verify', 'mobile']
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // FAMILY CARDS
  // ═══════════════════════════════════════════════════════════════════════════
  'family-cards': {
    title: 'Family Cards',
    content: `
# Family Cards

Link family members for group billing and shared account management.

## Overview
- Family Cards allow up to **6 members** (1 primary + 5 dependents) under one billing account.
- Registration fee: **₦25,000** for the card (covers all 6 members).
- Family Card ID format: **FC-YYYY-XXXX** (e.g., FC-2026-0001).
- Cards are **inactive** until payment is processed.

## Creating a New Family Card
1. Go to **Patient Registration** for the primary account holder.
2. In the **Payment & Family Cards** section, check "Enable family card for this patient".
3. Select **"Create New Family Card"**.
4. Enter the **Family Card Name** (e.g., "Johnson Family").
5. Complete registration - the patient becomes the **Primary Account Holder**.
6. A billing charge of ₦25,000 is created and sent to Accounts.
7. Once paid, the card becomes **Active** and members can be added.

## Linking a Patient to an Existing Family Card
1. Go to **Patient Registration** for the patient to be added.
2. In the **Payment & Family Cards** section, check "Enable family card".
3. Select **"Link to Existing Card"**.
4. Click **"Search Family Cards"** and search by:
   - Family Card Name
   - Primary holder's name or MRN
   - Family Card ID (FC-YYYY-XXXX)
5. Select the card (only **Active/Paid** cards can be selected).
6. Choose the **relationship** to the primary holder (Spouse, Child, Parent, etc.).
7. Complete registration - patient is linked to the family card.

## Member Limits & Fees
- **First 6 members** (including primary): Covered under the ₦25,000 Family Card fee.
- **7th member onwards**: Charged standard registration fee (₦5,000 per person).

## Payment Status
- **Pending/Unpaid**: Card is inactive. Cannot add members. Shows "Inactive: Contact Accounts".
- **Paid/Active**: Card is fully functional. Members can be added.

## Billing Mode
- **Master Pays All**: Primary account holder pays all family bills (default).
- Bills for all family members are consolidated under the primary holder.

## Managing Cards (Accounts Dashboard)
- View all Family Cards in the **Family Cards** panel.
- See aggregate outstanding balance for the family.
- Expand to view individual member balances.
- Process payments for family groups.

## Tips
- Only **Active** Family Cards appear in search results for linking.
- The primary holder cannot be removed without transferring ownership.
- Family Card badge appears on patient profiles showing their card and relationship.
    `,
    keywords: ['family', 'card', 'group', 'billing', 'members', 'link', 'primary', 'registration']
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // INVESTIGATIONS - EXPANDED
  // ═══════════════════════════════════════════════════════════════════════════
  'imaging-requests': {
    title: 'Imaging Requests',
    content: `
# Imaging Requests

Order and track imaging studies (X-ray, Ultrasound, CT, MRI).

## Creating an Imaging Request
1. Open the patient's record or consultation.
2. Click "New Imaging Request".
3. Select the modality (X-ray, Ultrasound, CT, MRI).
4. Choose the body part/region.
5. Enter clinical indication.
6. Set priority (Routine, Urgent, Emergency).
7. Submit the request.

## Request Status
- **Pending**: Awaiting scheduling.
- **Scheduled**: Appointment set with patient.
- **In Progress**: Examination underway.
- **Reported**: Images and report available.

## Viewing Results
1. Navigate to the patient's "Imaging" tab.
2. Click on a study to view images.
3. Read the radiologist's report.
4. Download or print if needed.

## Tips
- Include relevant clinical history for accurate interpretation.
- Mark as urgent only when clinically necessary.
    `,
    keywords: ['imaging', 'X-ray', 'ultrasound', 'CT', 'MRI', 'radiology']
  },

  'laboratory-results': {
    title: 'Viewing Laboratory Results',
    content: `
# Viewing Laboratory Results

Access and interpret patient lab results.

## Finding Results
1. Search for the patient by MRN or name.
2. Navigate to the "Lab Results" tab.
3. Results are listed chronologically (newest first).

## Understanding Results
- **Normal values** are shown in green.
- **Abnormal values** are highlighted in red or flagged.
- Reference ranges are displayed next to each value.

## Result Details
Click on a result to see:
- Test date and time
- Ordering doctor
- Specimen type
- Individual test values
- Historical trends (if available)

## Actions
- **Print**: Generate a printable report.
- **Share**: Send results to another provider.
- **Add to Notes**: Reference in clinical documentation.

## Critical Values
Critical results are automatically escalated and flagged for immediate review.
    `,
    keywords: ['lab results', 'blood test', 'values', 'abnormal', 'critical']
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // REFERRALS
  // ═══════════════════════════════════════════════════════════════════════════
  'referrals': {
    title: 'Patient Referrals',
    content: `
# Patient Referrals

Refer patients to specialists or external facilities.

## Creating a Referral
1. Open the patient's consultation.
2. Click "New Referral".
3. Select referral type:
   - Internal (within hospital)
   - External (to another facility)
4. Choose the specialty or department.
5. Enter clinical reason and urgency.
6. Attach relevant documents (optional).
7. Submit the referral.

## Tracking Referrals
- View status: Pending, Accepted, Completed, Cancelled.
- Receive notifications when the referral is processed.

## Receiving Referrals
If you receive referrals:
1. View incoming referrals in your dashboard.
2. Review patient history and reason.
3. Accept or decline with reason.
4. Schedule an appointment if accepted.

## Tips
- Include comprehensive clinical notes for effective referrals.
- Specify urgency clearly to prioritize appropriately.
    `,
    keywords: ['referral', 'specialist', 'transfer', 'consult']
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // EMERGENCY
  // ═══════════════════════════════════════════════════════════════════════════
  'emergency-registration': {
    title: 'Emergency Patient Registration',
    content: `
# Emergency Patient Registration

Quickly register patients in emergency situations.

## Quick Registration
When patient details are unknown or incomplete:
1. Click "Emergency Registration" or "Quick Register".
2. Enter any known information (name, approximate age, gender).
3. If unknown, use "Unknown Male/Female" as placeholder.
4. A temporary MRN is generated immediately.
5. Complete details can be updated later.

## Triage
1. Assign triage level:
   - **Red (Emergency)**: Life-threatening, immediate attention.
   - **Yellow (Urgent)**: Serious but not immediately life-threatening.
   - **Green (Non-Urgent)**: Minor, can wait.
2. Record presenting complaint.
3. Take initial vitals if possible.

## Updating Records
After stabilization:
1. Search for the temporary MRN.
2. Update patient demographics.
3. Link to existing record if patient is already registered.

## Tips
- Focus on critical care first, documentation second.
- Use wristbands with temporary MRN for identification.
    `,
    keywords: ['emergency', 'triage', 'quick', 'unknown', 'trauma']
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // BILLING - EXPANDED
  // ═══════════════════════════════════════════════════════════════════════════
  'billing-invoice': {
    title: 'Creating & Managing Invoices',
    content: `
# Creating & Managing Invoices

Generate and manage patient invoices.

## Creating an Invoice
1. Search for the patient.
2. Click "New Invoice" or "Bill Patient".
3. Add billable items:
   - Consultations
   - Procedures
   - Medications
   - Laboratory tests
   - Imaging
   - Bed charges (for admissions)
4. Apply discounts if authorized.
5. Generate the invoice.

## Invoice Status
- **Draft**: Being prepared.
- **Issued**: Sent to patient.
- **Partially Paid**: Some payment received.
- **Paid**: Fully settled.
- **Void**: Cancelled invoice.

## Applying Discounts
1. Select the line item or total.
2. Enter discount percentage or amount.
3. Provide authorization (if required).
4. Reason is logged for audit.

## Printing & Sharing
- Print a physical copy for the patient.
- Email the invoice directly.
- Generate a PDF for records.
    `,
    keywords: ['invoice', 'bill', 'charge', 'discount', 'fee']
  },

  'insurance-claims': {
    title: 'Insurance Claims',
    content: `
# Insurance Claims

Submit and track insurance claims.

## Submitting a Claim
1. Open the patient's billing record.
2. Click "Submit Claim".
3. Select the insurance provider.
4. Verify patient policy details.
5. Attach required documents (diagnoses, procedures).
6. Submit electronically or generate printable form.

## Claim Status
- **Submitted**: Sent to insurer.
- **Under Review**: Being processed.
- **Approved**: Claim accepted, payment pending.
- **Partially Approved**: Some items covered.
- **Rejected**: Claim denied (see reason).

## Handling Rejections
1. Review the rejection reason.
2. Correct any errors or add missing information.
3. Resubmit the claim.
4. Document all communications.

## Pre-Authorization
For procedures requiring prior approval:
1. Click "Request Pre-Auth".
2. Enter procedure details and clinical justification.
3. Submit and await response before proceeding.

## Reports
Generate reports for:
- Claims submitted by date range.
- Outstanding claims.
- Rejection rates by insurer.
    `,
    keywords: ['insurance', 'claim', 'NHIF', 'policy', 'coverage', 'pre-auth']
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // REPORTING & ANALYTICS
  // ═══════════════════════════════════════════════════════════════════════════
  'reports-analytics': {
    title: 'Reports & Analytics',
    content: `
# Reports & Analytics

Generate insights and reports from hospital data.

## Available Reports

### Operational Reports
- Daily patient census
- Bed occupancy rates
- Average wait times
- Staff productivity

### Financial Reports
- Revenue by department
- Outstanding balances
- Collection summary
- Service utilization

### Clinical Reports
- Diagnosis statistics
- Procedure volumes
- Prescription patterns
- Lab test volumes

## Generating a Report
1. Navigate to Reports section or Pulse Dashboard.
2. Select the report type.
3. Set date range and filters.
4. Click "Generate".
5. View, print, or export (CSV, PDF, Excel).

## Scheduling Reports
Some reports can be scheduled for automatic generation:
1. Select "Schedule Report".
2. Set frequency (daily, weekly, monthly).
3. Choose delivery method (email, dashboard).

## Dashboards
Real-time interactive dashboards are available for:
- Hospital overview
- Department-specific metrics
- Financial KPIs
    `,
    keywords: ['report', 'analytics', 'statistics', 'metrics', 'KPI', 'dashboard']
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // APPOINTMENTS
  // ═══════════════════════════════════════════════════════════════════════════
  'appointment-scheduling': {
    title: 'Appointment Scheduling',
    content: `
# Appointment Scheduling

Book and manage patient appointments.

## Booking an Appointment
1. Search for the patient (or register if new).
2. Click "Book Appointment".
3. Select the department or doctor.
4. Choose date and available time slot.
5. Select appointment type (consultation, follow-up, procedure).
6. Add notes if needed.
7. Confirm the booking.

## Viewing Schedules
- **Calendar View**: See appointments by day, week, or month.
- **List View**: Appointments in a table format.
- Filter by doctor, department, or status.

## Appointment Status
- **Scheduled**: Confirmed booking.
- **Checked In**: Patient has arrived.
- **In Progress**: Being seen.
- **Completed**: Visit concluded.
- **No Show**: Patient did not attend.
- **Cancelled**: Appointment was cancelled.

## Rescheduling
1. Find the appointment.
2. Click "Reschedule".
3. Select a new date/time.
4. Notify the patient (automatic SMS/email if configured).

## Cancellation
1. Find the appointment.
2. Click "Cancel".
3. Enter cancellation reason.
4. Patient is notified automatically.

## Tips
- Block time slots for breaks or meetings.
- Set buffer time between appointments if needed.
- Check for scheduling conflicts before confirming.
    `,
    keywords: ['appointment', 'booking', 'schedule', 'calendar', 'slot']
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // VITALS & OBSERVATIONS
  // ═══════════════════════════════════════════════════════════════════════════
  'vitals-recording': {
    title: 'Recording Vitals & Observations',
    content: `
# Recording Vitals & Observations

Document patient vital signs and clinical observations.

## Standard Vitals
Record the following measurements:
- **Temperature**: In Celsius or Fahrenheit.
- **Blood Pressure**: Systolic/Diastolic in mmHg.
- **Pulse**: Beats per minute.
- **Respiratory Rate**: Breaths per minute.
- **SpO2**: Oxygen saturation percentage.
- **Weight**: In kilograms.
- **Height**: In centimeters.

## Recording Process
1. Select the patient from ward view or search.
2. Click "Record Vitals".
3. Enter all measured values.
4. Add notes for any abnormalities.
5. Save the reading.

## Abnormal Values
- System flags values outside normal ranges.
- Critical values trigger alerts.
- Colors indicate severity (green, yellow, red).

## Trends
- View vital trends over time in graph format.
- Compare readings across visits.
- Identify patterns for clinical decision-making.

## Tips
- Record vitals at consistent times for accurate trends.
- Double-check readings before saving.
- Note the measurement method or device used if relevant.
    `,
    keywords: ['vitals', 'blood pressure', 'temperature', 'pulse', 'SpO2', 'observation']
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // DISCHARGE & CLINICAL SUMMARIES
  // ═══════════════════════════════════════════════════════════════════════════
  'discharge-summary': {
    title: 'Discharge Summary',
    content: `
# Discharge Summary

Complete the patient discharge process with proper documentation.

## Creating a Discharge Summary
1. Open the patient's admission record.
2. Click "Discharge" or "Prepare Discharge".
3. Complete the discharge summary:
   - Admission diagnosis
   - Procedures performed
   - Hospital course
   - Discharge diagnosis
   - Medications at discharge
   - Follow-up instructions
   - Diet and activity recommendations
4. Review all pending items (bills, prescriptions).
5. Finalize the discharge.

## Discharge Checklist
Before discharging, ensure:
- [ ] All bills are settled or arrangements made.
- [ ] Discharge medications are dispensed.
- [ ] Patient understands follow-up instructions.
- [ ] Next appointment is scheduled (if needed).
- [ ] Patient receives printed summary.

## Post-Discharge
- Bed becomes available for new admissions.
- Patient can access discharge summary via patient portal.
- Follow-up reminders are sent automatically.

## Medical Certificates
Generate certificates as needed:
- Sick leave certificate
- Fitness certificate
- Medical report
    `,
    keywords: ['discharge', 'summary', 'leave', 'certificate', 'follow-up']
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // NOTIFICATIONS & ALERTS
  // ═══════════════════════════════════════════════════════════════════════════
  'notifications': {
    title: 'Notifications & Alerts',
    content: `
# Notifications & Alerts

Stay updated with important system notifications.

## Notification Types
- **Clinical Alerts**: Critical lab results, vital abnormalities.
- **Task Reminders**: Due medications, scheduled follow-ups.
- **Administrative**: Approvals, system updates.
- **Messages**: Communications from colleagues.

## Notification Center
- Click the bell icon to view all notifications.
- Unread notifications are highlighted.
- Click to view details and take action.

## Managing Notifications
- **Mark as Read**: Click or view the notification.
- **Dismiss**: Remove from the list.
- **Snooze**: Delay reminder for later.

## Preferences
Configure what notifications you receive:
1. Go to Profile > Notification Preferences.
2. Toggle on/off by notification type.
3. Choose delivery method (in-app, email, SMS).

## Priority Levels
- **Critical**: Requires immediate attention (red indicator).
- **High**: Important but not emergency (orange).
- **Normal**: Routine notifications (blue).
- **Low**: Informational (gray).
    `,
    keywords: ['notification', 'alert', 'reminder', 'bell', 'message']
  },
  'whatsapp-support': {
    title: 'WhatsApp Support Community',
    content: `
# WhatsApp Support Community

The **CommunityCare KMC Support** WhatsApp group is an official channel for real-time support, system updates, and peer collaboration among KMC Hospital personnel.

## Purpose
- **Quick Support**: Get fast answers to system-related questions from IT support and super-users.
- **Updates**: Receive notifications about system maintenance, new features, and downtime.
- **Feedback**: Share your suggestions and report non-critical bugs.

## How to Join
Click the **WhatsApp** button in the help menu or footer to request access. *Note: Access is restricted to active KMC hospital staff only.*

## Community Regulations & Etiquette

### 1. Patient Privacy is Paramount (Zero Tolerance)
- **NO Patient Details**: Never share patient names, MRNs, diagnosis, or photos in the group.
- **Anonymize Data**: If posting a blurry screenshot for a bug report, you **MUST** blur out all patient-identifiable information.
- Violation of this rule may result in removal from the group and disciplinary action.

### 2. Professional Communication
- Keep discussions professional and constructive.
- Respect all members. Harassment or abusive language will not be tolerated.
- Avoid political or religious discussions; keep topics strictly related to hospital operations and the CommunityCare system.

### 3. Reporting Issues
- Before asking, search the group history to see if the question has been answered.
- **Format**: When reporting an issue, use this format:
  - **Module**: (e.g., Pharmacy)
  - **Issue**: (Short description)
  - **Error Message**: (If any)

### 4. No Spam
- Do not forward chain messages, "Good Morning" graphics, or promotional content.
- Keep the chat focused to ensure important support alerts are not missed.

## Official Support Hours
- **Mon-Fri**: 8:00 AM - 5:00 PM
- **Weekends/Holidays**: Emergency support only.

For critical system failures outside these hours, please contact the administrator directly.
    `,
    keywords: ['whatsapp', 'support', 'community', 'help', 'rules', 'contact']
  }
};
